import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, Send, User, LogOut } from "lucide-react";
import { API } from "@/config";
import { useAuth } from "@/context/AuthContext";

const LiveChat = () => {
  const { user, login, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [guestUser, setGuestUser] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [showGuestInput, setShowGuestInput] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const pollInterval = useRef(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/chat/messages?limit=100`);
      setMessages(res.data);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    pollInterval.current = setInterval(fetchMessages, 3000);
    return () => clearInterval(pollInterval.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only poll setup

  useEffect(() => {
    scrollToBottom();
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps -- scroll on new messages

  const activeUser = user || guestUser;

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeUser || sending) return;

    setSending(true);
    try {
      if (guestUser) {
        await axios.post(`${API}/chat/messages/guest`, {
          message: newMessage.trim(),
          user_name: guestUser.name
        });
      } else {
        await axios.post(
          `${API}/chat/messages`,
          { message: newMessage.trim() },
          { withCredentials: true }
        );
      }
      setNewMessage('');
      fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      if (err.response?.status === 401) {
        alert('Please sign in to send messages');
      }
    } finally {
      setSending(false);
    }
  };

  const handleGuestLogin = (e) => {
    e.preventDefault();
    if (!guestName.trim() || guestName.trim().length < 2) return;
    setGuestUser({ name: guestName.trim(), picture: null });
    setShowGuestInput(false);
  };

  const handleLogout = () => {
    if (guestUser) {
      setGuestUser(null);
      setGuestName('');
    } else {
      logout();
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="live-chat" data-testid="live-chat">
      <div className="live-chat-header">
        <MessageCircle size={20} />
        <span>Game Day Chat</span>
        <span className="chat-online-count">
          <span className="online-dot"></span>
          Live
        </span>
      </div>

      <div className="live-chat-messages" ref={chatContainerRef}>
        {loading ? (
          <div className="chat-loading">
            <div className="loading-spinner" style={{ width: 24, height: 24 }}></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">
            <MessageCircle size={32} />
            <p>No messages yet. Be the first to chat!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className="chat-message">
              <img 
                src={msg.user_picture || 'https://via.placeholder.com/32'} 
                alt={msg.user_name}
                className="chat-avatar"
              />
              <div className="chat-message-content">
                <div className="chat-message-header">
                  <span className="chat-username">{msg.user_name}</span>
                  <span className="chat-time">{formatTime(msg.created_at)}</span>
                </div>
                <p className="chat-text">{msg.message}</p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} style={{ height: 1 }} />
      </div>

      <div className="live-chat-input">
        {activeUser ? (
          <form onSubmit={handleSendMessage} className="chat-form">
            <div className="chat-user-info">
              {activeUser.picture ? (
                <img src={activeUser.picture} alt={activeUser.name} />
              ) : (
                <div className="chat-avatar-placeholder"><User size={14} /></div>
              )}
              <span>{activeUser.name}</span>
              <button type="button" onClick={handleLogout} className="chat-logout-btn">
                <LogOut size={14} />
              </button>
            </div>
            <div className="chat-input-row">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                maxLength={500}
                disabled={sending}
                data-testid="chat-input"
              />
              <button type="submit" disabled={!newMessage.trim() || sending} data-testid="chat-send-btn">
                <Send size={18} />
              </button>
            </div>
          </form>
        ) : showGuestInput ? (
          <form onSubmit={handleGuestLogin} className="chat-guest-form">
            <p>Choose a display name:</p>
            <div className="chat-input-row">
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Your name..."
                maxLength={30}
                autoFocus
                data-testid="guest-name-input"
              />
              <button type="submit" disabled={!guestName.trim() || guestName.trim().length < 2} data-testid="guest-join-btn">
                <Send size={18} />
              </button>
            </div>
            <button type="button" className="chat-back-btn" onClick={() => setShowGuestInput(false)}>
              Back
            </button>
          </form>
        ) : (
          <div className="chat-login-prompt">
            <p>Join the chat</p>
            <button onClick={login} className="btn btn-primary chat-login-btn" data-testid="chat-login-btn">
              <User size={16} />
              Sign in with Google
            </button>
            <button onClick={() => setShowGuestInput(true)} className="btn btn-secondary chat-login-btn" data-testid="chat-guest-btn">
              <User size={16} />
              Join as Guest
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveChat;
